import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors — keep return path so checkout can resume after login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      const path = window.location.pathname + window.location.search;
      if (!path.startsWith('/login') && !path.startsWith('/register')) {
        const redirect = encodeURIComponent(path || '/dashboard');
        window.location.href = `/login?redirect=${redirect}`;
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth API
export const authAPI = {
  register: (email: string, password: string) =>
    api.post('/auth/register', { email, password }),
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  requestPasswordReset: (email: string) =>
    api.post('/auth/request-password-reset', { email }),
  resetPassword: (token: string, password: string) =>
    api.post('/auth/reset-password', { token, password }),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),
};

// Matrix API
export const matrixAPI = {
  calculate: (name: string, birthDate: string) =>
    api.post('/matrix/calculate', { name, birthDate }),
  save: (name: string, birthDate: string, matrixData: any) =>
    api.post('/matrix/save', { name, birthDate, matrixData }),
  getHistory: () => api.get('/matrix/history'),
};

// Chat API
export const chatAPI = {
  createSession: (matrixCalculationId?: number) =>
    api.post('/chat/sessions', { matrixCalculationId }),
  getSessions: () => api.get('/chat/sessions'),
  sendMessage: (sessionId: number, message: string) =>
    api.post(`/chat/sessions/${sessionId}/messages`, { message }),
  getMessages: (sessionId: number) =>
    api.get(`/chat/sessions/${sessionId}/messages`),
};

// Subscription API
export type CheckoutPlanType = 'basic' | 'pro' | 'premium' | 'full2026';
export const subscriptionAPI = {
  getSubscription: () => api.get('/subscription'),
  createCheckoutSession: (planType: CheckoutPlanType) =>
    api.post<{ url: string }>('/subscription/create-checkout-session', { planType }),
  confirmSession: (sessionId: string) =>
    api.post<{ subscription: any; activated: boolean }>('/subscription/confirm-session', { sessionId }),
  createBillingPortal: () =>
    api.post<{ url: string }>('/subscription/billing-portal'),
  createSubscription: (planType: CheckoutPlanType) =>
    api.post('/subscription', { planType }),
  cancelSubscription: () => api.post('/subscription/cancel'),
};

// Calendar API (Life Decision Calendar)
export const calendarAPI = {
  getScores: (from: string, to: string, locale?: string) =>
    api.get('/calendar/scores', { params: { from, to, ...(locale && { locale }) } }),
  getToday: (locale?: string) =>
    api.get('/calendar/today', { params: locale ? { locale } : {} }),
};

// User API
export const userAPI = {
  getProfile: () => api.get('/user/profile'),
  getCertificate: () =>
    api.get<{
      certificate: {
        title: string;
        titleRu: string;
        holderName: string;
        email: string;
        planType: string;
        issuedAt: string;
        brand: string;
      };
    }>('/user/certificate'),
};

// Status API (режимы оплаты/БД)
export type AppStatus = {
  database: 'connected' | 'memory';
  virtualPayment: boolean;
  stripeConfigured?: boolean;
  stripeTestMode: boolean;
  stripePricesConfigured?: boolean;
};
export const statusAPI = {
  get: () => api.get<AppStatus>('/status'),
};
